import type { PremiumPlan, PremiumPlanId } from '~/types/premium'

/**
 * Single source of truth for prices. Every displayed price and every
 * "you save X %" is derived from these numbers; nothing is hardcoded in
 * components or translations any more.
 *
 * Variant ids live in runtimeConfig.public (see nuxt.config.ts) so the same
 * code can point at test/production LemonSqueezy products.
 */
export const PRICES = {
  /** Legacy monthly subscription. Still billed for existing customers, no longer sold. */
  monthly: 6.9,
  yearly: 69,
  exportCredit: 4.9,
  /** One CV tailored to one posting, as one clean PDF (sold after the tailoring preview). */
  tailoredPdf: 9.9,
  /** Interview prep for the posting a tailored PDF was bought for (sold right after it). */
  interviewPrep: 7.9,
  /**
   * The application kit: one tailored PDF, the cover letter and the interview
   * prep for the same posting, in one payment (sold where the tailored PDF is).
   * Less than the two sold on their own (APPLICATION_KIT_SEPARATE), and the
   * cover letter is not sold on its own at all.
   */
  applicationKit: 14.9,
  jobPack: 29,
  /** Premium month by month, sold only to someone whose pack is ending or just ended (PACK_CONTINUATION). */
  packMonthly: 14.9,
} as const

/**
 * The same products in Czech crowns, sold to visitors who read the site in
 * Czech through Stripe (server/api/stripe/*), with ZakWeb s.r.o. as the
 * seller. Not a conversion of PRICES: prices a Czech buyer reads as round
 * (…9 Kč), set on their own. The operator is not a VAT payer
 * (constants/legal/operator.ts), so these are final prices without VAT.
 * Every key of PRICES has one, so a price list can be swapped as a whole.
 */
export const PRICES_CZK: { readonly [K in keyof typeof PRICES]: number } = {
  monthly: 159,
  yearly: 1590,
  exportCredit: 119,
  tailoredPdf: 229,
  interviewPrep: 179,
  applicationKit: 349,
  jobPack: 690,
  packMonthly: 349,
}

/**
 * The same products in Polish złoty, sold to visitors who read the site in
 * Polish through Stripe, with BLIK and Przelewy24 next to the card
 * (server/api/stripe/create-checkout-session.post.ts). Set the way Polish
 * shops price (…,99 zł, whole złoty for the pack), close to the Czech list at
 * about 5.8 Kč to the złoty, and keeping its shape: the kit costs less than
 * its parts, and the pack pays off from the 3rd posting (packBreakEven).
 */
export const PRICES_PLN: { readonly [K in keyof typeof PRICES]: number } = {
  monthly: 27.99,
  yearly: 279,
  exportCredit: 19.99,
  tailoredPdf: 39.99,
  interviewPrep: 29.99,
  applicationKit: 59.99,
  jobPack: 119,
  packMonthly: 59.99,
}

/** The currencies we sell in: dollars from LemonSqueezy, crowns and złoty from Stripe. GBP is shown only. */
export type Currency = 'USD' | 'GBP' | 'CZK' | 'PLN'
/** What a checkout can charge: dollars (LemonSqueezy), crowns or złoty (Stripe). */
export type CheckoutCurrency = 'USD' | 'CZK' | 'PLN'
/** The currencies Stripe takes, each sold to the readers of one language. */
export type StripeCurrency = Exclude<CheckoutCurrency, 'USD'>
export const STRIPE_CURRENCIES = ['CZK', 'PLN'] as const satisfies readonly StripeCurrency[]

export function isStripeCurrency(value: unknown): value is StripeCurrency {
  return (STRIPE_CURRENCIES as readonly unknown[]).includes(value)
}

/**
 * Which currency a visitor buys in: Czech crowns when they read the site in
 * Czech and the Czech checkout is switched on (NUXT_PUBLIC_STRIPE_CZK=1),
 * Polish złoty when they read it in Polish and the Polish checkout is on
 * (NUXT_PUBLIC_STRIPE_PLN=1), dollars otherwise. The same rule decides what
 * they are shown, what the checkout charges and what the e-mails about the
 * purchase quote.
 */
export function checkoutCurrency(locale: string | null | undefined, czkEnabled: unknown, plnEnabled: unknown = false): CheckoutCurrency {
  if (locale === 'cs' && isSwitchOn(czkEnabled)) return 'CZK'
  if (locale === 'pl' && isSwitchOn(plnEnabled)) return 'PLN'
  return 'USD'
}

/** runtimeConfig.public.stripeCzk / stripePln as a boolean (the environment may give it as a string). */
export function isSwitchOn(value: unknown): boolean {
  return value === true || value === '1' || value === 'true' || value === 1
}

/** Real discount code configured in LemonSqueezy, tied to the legacy monthly plan. */
export const PRESELL = {
  code: 'CWMZU3NQ',
  price: 3.45,
  /** How long the countdown offer stays open, in seconds. */
  durationSeconds: 600,
} as const

export const JOB_PACK_DAYS = 30

/**
 * The welcome series' code (server/utils/welcomeCode.ts): one per address, a
 * real LemonSqueezy discount on the Job Search Pack alone, one use, and it
 * really stops working `hours` after the day-1 e-mail that brings it. Not a
 * fake countdown: the letter shows the moment LemonSqueezy expires it.
 * Off until WELCOME_CODE_ENABLED=1.
 */
export const WELCOME_CODE = {
  /** The pack with the code. */
  price: 19.9,
  /** The same in Czech crowns and Polish złoty, a Stripe promotion code (server/utils/stripeDiscount.ts). */
  priceCzk: 449,
  pricePln: 79,
  hours: 72,
  /** Codes read HELLO-7KQ2MX: the prefix tells them from partners' codes. */
  prefix: 'HELLO',
  /** No 0/O or 1/I, so a code read off a phone is typed right. */
  alphabet: 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789',
  /** The query parameter that carries it from the e-mail to the checkout. */
  param: 'promo',
} as const

/** A welcome code (HELLO-7KQ2MX) in upper case, or null for anything else: what a link may prefill. */
export function normalizeWelcomeCode(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const code = value.trim().toUpperCase()
  return new RegExp(`^${WELCOME_CODE.prefix}-[${WELCOME_CODE.alphabet}]{6}$`).test(code) ? code : null
}

/**
 * The Civil Service application (/civil-service-application): the personal
 * statement and behaviour statements for one UK Civil Service advert. Sold to
 * UK applicants, but in dollars from the main store like everything else:
 * LemonSqueezy did not approve a GBP store. Writers charge £100+ for the same
 * statements; this is the price a sift-stage applicant pays without thinking
 * twice, tested with Google Ads before anything else is built on it.
 */
export const CIVIL_SERVICE_PRICE = { amount: 14.9, currency: 'USD' } as const

/**
 * The NHS supporting statement (/nhs-supporting-statement): the evidence for
 * every criterion of one NHS advert, box by box on NHS Jobs or as one
 * statement elsewhere. UK applicants, in dollars like the Civil Service
 * application. Lower than that: one text rather than a set of statements,
 * and most NHS adverts are for bands 2 to 5.
 */
export const NHS_STATEMENT_PRICE = { amount: 9.9, currency: 'USD' } as const

/**
 * The way on after a pack: Premium for PRICES.packMonthly a month, renewing
 * until cancelled. Sold only to someone whose pack ends within `opensDaysBefore`
 * or ended less than `closesDaysAfter` ago, and who has no subscription. It
 * starts on the day it is bought, so it opens just a day before the end: a
 * subscription bought earlier would pay for days the pack already covers.
 * The pack's last e-mails (server/utils/packFollowup.ts) offer it.
 */
export const PACK_CONTINUATION = {
  opensDaysBefore: 1,
  closesDaysAfter: 14,
} as const

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * When the continuation offer closes for this account, or null when it is not
 * open: no pack, a subscription already, too early or too late.
 */
export function packContinuationCloses(premiumUntil: string | Date | null | undefined, subscriptionActive: boolean, now: number = Date.now()): Date | null {
  if (subscriptionActive || !premiumUntil) return null
  const end = new Date(premiumUntil).getTime()
  if (Number.isNaN(end)) return null
  const opens = end - PACK_CONTINUATION.opensDaysBefore * DAY_MS
  const closes = end + PACK_CONTINUATION.closesDaysAfter * DAY_MS
  return now >= opens && now < closes ? new Date(closes) : null
}

/**
 * What the Job Search Pack unlocks, as the lines the offer shows. Only
 * things a free account really cannot do belong here: the Basic template is
 * clean for everyone and the others are free with a watermark, so "cvs" sells
 * the designed templates; a daily job match and a check of an uploaded CV are
 * free too (score and top findings), so "checks" sells the full reports and
 * the editor's ATS check, which is premium only. "interview" is the interview
 * prep for each posting, sold on its own for PRICES.interviewPrep.
 */
export const JOB_PACK_STACK = ['cvs', 'checks', 'writing', 'interview', 'web'] as const
export type JobPackStackLine = typeof JOB_PACK_STACK[number]

/**
 * What a limit or an upsell was about. It picks the checkout modal's heading
 * (`premium.jobPack.lead.*`) and which stack line goes first.
 */
export const JOB_PACK_LEADS = ['clean', 'versions', 'ats', 'coverLetters', 'web', 'ai', 'interview', 'tracking'] as const
export type JobPackLead = typeof JOB_PACK_LEADS[number]

export const JOB_PACK_LEAD_LINE: Record<JobPackLead, JobPackStackLine> = {
  clean: 'cvs',
  versions: 'cvs',
  ats: 'checks',
  coverLetters: 'writing',
  ai: 'writing',
  web: 'web',
  interview: 'interview',
  // "Was my CV opened?": the link for one company is sharing, like the web CV.
  tracking: 'web',
}

/**
 * What the market charges for what the pack does, shown next to its price.
 * Real prices, never an invented "worth $X" total, and never a competitor's
 * name in the offer (EU comparative advertising must stay objective and
 * verifiable). The one place competitors are named is /alternatives/*, from
 * constants/competitors.ts, under the same sourcing rules. Checked 2026-09-23;
 * recheck every quarter, together with that file.
 */
export const MARKET_ANCHORS = {
  /**
   * CV builders that start with a cheap trial and renew every 4 weeks (13
   * charges a year): Zety about $23.95–25.95, Resume.io $29.95.
   * https://resufit.com/blog/zety-review-pricing-is-it-worth-it/
   * https://ophyai.com/blog/resume-writing/best-resume-builder-websites-pricing-comparison-2026
   */
  cvBuilderEvery4Weeks: { low: 23.95, high: 29.95 },
  /**
   * Job-match / ATS scanners, the same check as our ATS report and job match:
   * Jobscan Premium $49.95 a month. https://pitchmeai.com/blog/jobscan-pricing-plans
   */
  jobMatchToolMonthly: 49.95,
  /**
   * Website builders' cheapest plan for a personal site with your own link:
   * Squarespace Personal $16/mo (annual), Wix from $17/mo. Our web CV is online
   * for the pack's 30 days, so the comparison is one month to one month.
   * https://www.websitebuilderexpert.com/website-builders/squarespace-pricing/
   * https://hostadvice.com/blog/website-builders/wix/wix-vs-squarespace-pricing/
   */
  personalWebsiteMonthly: 16,
} as const

/** "Applying to 20 jobs this month? That's $1.45 per application." */
export const JOB_PACK_APPLICATIONS = 20

/**
 * From which posting the pack costs less than our own tailored PDFs bought one
 * by one ($9.90 × 3 = $29.70 > $29): "worth it from the 3rd job ad". Our own
 * prices only, so the e-mails can say it without any market claim.
 */
export function packBreakEven(currency: CheckoutCurrency = 'USD'): number {
  const { prices } = pricingFor(currency)
  return Math.floor(prices.jobPack / prices.tailoredPdf) + 1
}

/**
 * "Bought separately: about $90 a month." The cheapest plan of each tool the
 * pack replaces, summed and rounded, never called a "saving": nobody buys all
 * three, and one of them bills every 4 weeks rather than monthly (the page's
 * footnote says so).
 */
export function separateToolsMonthly(): number {
  return Math.round(MARKET_ANCHORS.cvBuilderEvery4Weeks.low + MARKET_ANCHORS.jobMatchToolMonthly + MARKET_ANCHORS.personalWebsiteMonthly)
}

export interface PublicVariantConfig {
  lsVariantMonthly?: string | number
  lsVariantYearly?: string | number
  lsVariantExport?: string | number
  lsVariantJobPack?: string | number
  lsVariantTailored?: string | number
  lsVariantInterview?: string | number
  lsVariantPackMonthly?: string | number
  lsVariantCivilService?: string | number
  lsVariantNhsStatement?: string | number
  lsVariantApplicationKit?: string | number
}

/**
 * A variant id as a string. Nuxt reads a numeric NUXT_PUBLIC_LS_VARIANT_*
 * from the environment as a number, while checkouts and webhooks look plans
 * up by the string: without this, a plan set only through the environment
 * is "Unknown plan variant" at checkout.
 */
function variantIdOf(value: string | number | null | undefined): string {
  return value == null ? '' : String(value).trim()
}

/**
 * Formats a USD amount the way the site shows prices: whole dollars without
 * cents (`$29`, `$69`), everything else with two decimals (`$4.90`, `$1.45`).
 */
export function formatUsd(amount: number): string {
  const cents = Math.round(amount * 100)
  // A deduction reads "-$14.50", not "$-14.50".
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  return abs % 100 === 0 ? `${sign}$${abs / 100}` : `${sign}$${(abs / 100).toFixed(2)}`
}

const CURRENCY_SYMBOLS = { USD: '$', GBP: '£' } as const

/**
 * Czech crowns the Czech way: `690 Kč`, `1 590 Kč`, `34,50 Kč`, with a
 * non-breaking space between thousands and before the symbol.
 */
export function formatCzk(amount: number): string {
  const cents = Math.round(amount * 100)
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  const whole = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0')
  const rest = abs % 100 === 0 ? '' : `,${String(abs % 100).padStart(2, '0')}`
  return `${sign}${whole}${rest}\u00A0Kč`
}

/**
 * Polish złoty the Polish way: `119 zł`, `39,99 zł`, `1590 zł`, `12 900 zł`.
 * Thousands are spaced (non-breaking) only from five digits on, as Polish
 * typography does, and a non-breaking space comes before the symbol.
 */
export function formatPln(amount: number): string {
  const cents = Math.round(amount * 100)
  const sign = cents < 0 ? '-' : ''
  const abs = Math.abs(cents)
  const digits = String(Math.floor(abs / 100))
  const whole = digits.length > 4 ? digits.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00A0') : digits
  const rest = abs % 100 === 0 ? '' : `,${String(abs % 100).padStart(2, '0')}`
  return `${sign}${whole}${rest}\u00A0zł`
}

/** formatUsd for any currency we sell in: `£14.90`, `$29`, `690 Kč`, `119 zł`. */
export function formatPrice(amount: number, currency: Currency = 'USD'): string {
  if (currency === 'CZK') return formatCzk(amount)
  if (currency === 'PLN') return formatPln(amount)
  return formatUsd(amount).replace('$', CURRENCY_SYMBOLS[currency])
}

/**
 * One currency's price list and everything derived from it, so a page, an
 * e-mail or a checkout can be written once and show dollars or crowns.
 */
export interface Pricing {
  currency: CheckoutCurrency
  prices: { readonly [K in keyof typeof PRICES]: number }
  /** The pack never costs less than this after credits. */
  packMinPrice: number
  /** The pack with the welcome code. */
  welcomePrice: number
  /** What the application kit's parts cost bought one by one. */
  applicationKitSeparate: number
  format: (amount: number) => string
}

/** A currency's price list, the pack's floor and the welcome code's price. */
function priceListOf(currency: CheckoutCurrency): Pick<Pricing, 'prices' | 'packMinPrice' | 'welcomePrice'> {
  if (currency === 'CZK') return { prices: PRICES_CZK, packMinPrice: PACK_MIN_PRICE_CZK, welcomePrice: WELCOME_CODE.priceCzk }
  if (currency === 'PLN') return { prices: PRICES_PLN, packMinPrice: PACK_MIN_PRICE_PLN, welcomePrice: WELCOME_CODE.pricePln }
  return { prices: PRICES, packMinPrice: PACK_MIN_PRICE, welcomePrice: WELCOME_CODE.price }
}

export function pricingFor(currency: CheckoutCurrency = 'USD'): Pricing {
  const { prices, packMinPrice, welcomePrice } = priceListOf(currency)
  return {
    currency,
    prices,
    packMinPrice,
    welcomePrice,
    applicationKitSeparate: Math.round((prices.tailoredPdf + prices.interviewPrep) * 100) / 100,
    format: amount => formatPrice(amount, currency),
  }
}

/**
 * Paid for one posting, taken off the Job Search Pack when the same person
 * buys it later (server/utils/packCredit.ts): the tailored PDF and the
 * interview prep, each once, at its list price.
 */
export const PACK_CREDIT_PLANS = { 'tailored-pdf': PRICES.tailoredPdf, 'interview-prep': PRICES.interviewPrep, 'application-kit': PRICES.applicationKit } as const
export type PackCreditPlan = keyof typeof PACK_CREDIT_PLANS
/** PACK_CREDIT_PLANS in the currency the pack is bought in: an earlier purchase counts at that list's price. */
export function packCreditPlans(currency: CheckoutCurrency = 'USD'): Record<PackCreditPlan, number> {
  const prices = pricingFor(currency).prices
  return { 'tailored-pdf': prices.tailoredPdf, 'interview-prep': prices.interviewPrep, 'application-kit': prices.applicationKit }
}
/**
 * What the kit's parts cost bought one by one: the tailored PDF and the
 * interview prep (the cover letter is not sold on its own). A real sum of our
 * own prices, so the kit may be shown next to it.
 */
export const APPLICATION_KIT_SEPARATE = Math.round((PRICES.tailoredPdf + PRICES.interviewPrep) * 100) / 100

/** `guest_exports.bundle` of a tailored PDF bought as the application kit. */
export const APPLICATION_KIT_BUNDLE = 'application-kit'

/** The pack never costs less than this, however much was bought before. */
export const PACK_MIN_PRICE = 9.9
/** PACK_MIN_PRICE in Czech crowns: the tailored PDF's price, as in dollars. */
export const PACK_MIN_PRICE_CZK = 229
/** PACK_MIN_PRICE in Polish złoty: the tailored PDF's price, as in dollars. */
export const PACK_MIN_PRICE_PLN = 39.99

/** The pack's price after `credit` already paid (in `currency`), never below its minimum. */
export function packPriceAfterCredit(credit: number, currency: CheckoutCurrency = 'USD'): number {
  const { prices, packMinPrice } = pricingFor(currency)
  return Math.max(packMinPrice, Math.round((prices.jobPack - Math.max(0, credit)) * 100) / 100)
}

/** Whole-number discount, e.g. discountPercent(6.9, 3.45) === 50. */
export function discountPercent(original: number, current: number): number {
  if (original <= 0 || current >= original) return 0
  return Math.round(((original - current) / original) * 100)
}

/**
 * Which price list entry a plan sells for in crowns or złoty. Plans missing
 * here (the UK-only Civil Service application and NHS statement) are sold in
 * dollars through LemonSqueezy in every language.
 */
const STRIPE_PRICE_KEY: Partial<Record<PremiumPlanId, keyof typeof PRICES>> = {
  'one-time': 'exportCredit',
  'tailored-pdf': 'tailoredPdf',
  'interview-prep': 'interviewPrep',
  'application-kit': 'applicationKit',
  'job-pack': 'jobPack',
  'pack-monthly': 'packMonthly',
  'yearly': 'yearly',
  'monthly': 'monthly',
}

/** The plan as sold in `currency`: in crowns or złoty through Stripe where it has a price there. */
function inCurrency(plan: PremiumPlan, currency: CheckoutCurrency): PremiumPlan {
  const key = STRIPE_PRICE_KEY[plan.id]
  if (currency === 'USD' || !key) return plan
  return { ...plan, price: pricingFor(currency).prices[key], currency, provider: 'stripe' }
}

/**
 * Every plan the code knows about, including ones we no longer sell.
 * Plans whose variant id is not configured are omitted, so an unfinished
 * product can never reach checkout. The LemonSqueezy variant is that switch
 * in crowns and złoty too: a product is on sale in Czech and Polish once it is on sale at all.
 */
export function getAllPlans(config: PublicVariantConfig, currency: CheckoutCurrency = 'USD'): PremiumPlan[] {
  const plans: PremiumPlan[] = [
    {
      id: 'one-time',
      variantId: variantIdOf(config.lsVariantExport),
      offered: true,
      price: PRICES.exportCredit,
      currency: 'USD',
      interval: null,
      isOneTime: true,
      grantsExportCredit: true,
      nameKey: 'premium.upsell.oneTimeExport.title',
      descriptionKey: 'premium.upsell.oneTimeExport.description',
      badgeKey: 'premium.upsell.oneTimeExport.badge',
    },
    {
      // The CV rewritten for the posting the visitor just pasted, as one clean
      // PDF. Sold only under that preview (LandingTailorDemo), never in a plan
      // list: what it unlocks is a tailoring parked on the server, which the
      // checkout cannot reach any other way.
      id: 'tailored-pdf',
      variantId: variantIdOf(config.lsVariantTailored),
      offered: true,
      contextual: true,
      price: PRICES.tailoredPdf,
      currency: 'USD',
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      nameKey: 'premium.tailoredPdf.title',
      descriptionKey: 'premium.tailoredPdf.description',
    },
    {
      // Interview prep for the posting a tailored PDF was just bought for.
      // Sold only on that purchase (InterviewPrepOffer), never in a plan list:
      // the prep is written from the posting parked with that tailored CV.
      id: 'interview-prep',
      variantId: variantIdOf(config.lsVariantInterview),
      offered: true,
      contextual: true,
      price: PRICES.interviewPrep,
      currency: 'USD',
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      nameKey: 'premium.interviewPrep.title',
      descriptionKey: 'premium.interviewPrep.description',
    },
    {
      // The tailored PDF with the cover letter and the interview prep for its
      // posting. Bought on the token the tailoring preview parked, like the
      // tailored PDF alone; the webhook marks that row as the kit (`bundle`).
      id: 'application-kit',
      variantId: variantIdOf(config.lsVariantApplicationKit),
      offered: true,
      contextual: true,
      price: PRICES.applicationKit,
      currency: 'USD',
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      nameKey: 'premium.applicationKit.title',
      descriptionKey: 'premium.applicationKit.description',
    },
    {
      // Personal statement and behaviour statements for one UK Civil Service
      // advert. Sold only on its own page (/civil-service-application), on the
      // token its preview was parked under.
      id: 'civil-service',
      variantId: variantIdOf(config.lsVariantCivilService),
      offered: true,
      contextual: true,
      price: CIVIL_SERVICE_PRICE.amount,
      currency: CIVIL_SERVICE_PRICE.currency,
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      nameKey: 'premium.civilService.title',
      descriptionKey: 'premium.civilService.description',
    },
    {
      // The supporting statement for one NHS advert. Sold like the Civil
      // Service application: on its own page (/nhs-supporting-statement), on
      // the token its preview was parked under.
      id: 'nhs-statement',
      variantId: variantIdOf(config.lsVariantNhsStatement),
      offered: true,
      contextual: true,
      price: NHS_STATEMENT_PRICE.amount,
      currency: NHS_STATEMENT_PRICE.currency,
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      nameKey: 'premium.nhsStatement.title',
      descriptionKey: 'premium.nhsStatement.description',
    },
    {
      id: 'job-pack',
      variantId: variantIdOf(config.lsVariantJobPack),
      offered: true,
      price: PRICES.jobPack,
      currency: 'USD',
      interval: null,
      isOneTime: true,
      grantsExportCredit: false,
      grantsPremiumDays: JOB_PACK_DAYS,
      nameKey: 'premium.jobPack.title',
      descriptionKey: 'premium.jobPack.description',
      badgeKey: 'premium.jobPack.badge',
      recommended: true,
    },
    {
      // Premium month by month after a pack (PACK_CONTINUATION). Sold only
      // while that offer is open: the checkout and the plan lists check it.
      id: 'pack-monthly',
      variantId: variantIdOf(config.lsVariantPackMonthly),
      offered: true,
      contextual: true,
      price: PRICES.packMonthly,
      currency: 'USD',
      interval: 'month',
      isOneTime: false,
      grantsExportCredit: false,
      nameKey: 'premium.packMonthly.title',
      descriptionKey: 'premium.packMonthly.description',
      badgeKey: 'premium.packMonthly.badge',
    },
    {
      id: 'yearly',
      variantId: variantIdOf(config.lsVariantYearly),
      offered: true,
      price: PRICES.yearly,
      currency: 'USD',
      interval: 'year',
      isOneTime: false,
      grantsExportCredit: false,
      nameKey: 'premium.yearlyPlan',
      descriptionKey: 'premium.upsell.subheading.default',
      badgeKey: 'premium.badges.yearly',
    },
    {
      // Retired: a monthly subscription cancelled on day one gave the same 30 days
      // as the Job Search Pack for a fraction of the price. Kept so renewals and
      // old links still resolve to a known plan.
      id: 'monthly',
      variantId: variantIdOf(config.lsVariantMonthly),
      offered: false,
      price: PRICES.monthly,
      currency: 'USD',
      interval: 'month',
      isOneTime: false,
      grantsExportCredit: false,
      nameKey: 'premium.monthlyPlan',
      descriptionKey: 'premium.upsell.subheading.default',
      badgeKey: 'premium.badges.monthly',
    },
  ]

  return plans.filter(plan => plan.variantId !== '').map(plan => inCurrency(plan, currency))
}

/** Plans a plan list (pricing, checkout modal, upsells) may show and sell. */
export function getPlans(config: PublicVariantConfig, currency: CheckoutCurrency = 'USD'): PremiumPlan[] {
  return getAllPlans(config, currency).filter(plan => plan.offered && !plan.contextual)
}

/** A plan on sale, including one sold only in its own flow (`contextual`). */
export function getPlan(config: PublicVariantConfig, id: PremiumPlanId, currency: CheckoutCurrency = 'USD'): PremiumPlan | undefined {
  return getAllPlans(config, currency).find(plan => plan.offered && plan.id === id)
}

/** Reverse lookup used by the webhook: which plan does this LemonSqueezy variant belong to? */
export function getPlanByVariant(config: PublicVariantConfig, variantId: string | number): PremiumPlan | undefined {
  const id = variantIdOf(variantId)
  return getAllPlans(config).find(plan => plan.variantId === id)
}

/** Yearly price expressed per month, e.g. 5.75. */
export function yearlyPerMonth(currency: CheckoutCurrency = 'USD'): number {
  return pricingFor(currency).prices.yearly / 12
}
